import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useArtistAuth } from './ArtistAuthContext';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';
import {
  acknowledgeAnnouncement,
  getAdminAnnouncementPendingCount,
  listAnnouncementFeed,
} from '../api/announcements';

const AnnouncementContext = createContext(null);
const POPUP_ACK_PREFIX = 'CASTICART_ANNOUNCEMENT_ACK_';
const POPUP_COOKIE_TTL_DAYS = 90;
const LATEST_FEED_LIMIT = 5;
const POPUP_FEED_LIMIT = 100;
const COOKIE_OPTIONS = '; path=/';

const buildCookieName = (role) => `${POPUP_ACK_PREFIX}${role || 'default'}`.toUpperCase();

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value, days = POPUP_COOKIE_TTL_DAYS) => {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value || '')}; expires=${expires}${COOKIE_OPTIONS}`;
};

const buildPopupToken = (announcement) => {
  if (!announcement) return null;
  const timestamp =
    announcement.publishedAt ||
    announcement.primaryPublishedAt ||
    announcement.publishAt ||
    announcement.createdAt;
  if (!timestamp) return null;
  return `${announcement.id}:${new Date(timestamp).toISOString()}`;
};

export function AnnouncementProvider({ children }) {
  const { user } = useAuth();
  const { artistUser } = useArtistAuth();
  const isTaskOnlyAdmin =
    user?.isLoggedIn &&
    user?.userType === 'admin' &&
    String(user?.role || '').toLowerCase() === 'task_only';

  const activeRole = isTaskOnlyAdmin
    ? null
    : user?.isLoggedIn
    ? 'admin'
    : artistUser?.isLoggedIn
      ? 'artist'
      : null;
  const canViewAdminAnnouncements = useMemo(() => {
    if (!user?.isLoggedIn || activeRole !== 'admin') return false;
    return resolveAdminPermission({
      permissionKey: 'NAV_ANNOUNCEMENTS',
      role: user?.role,
      permissions: user?.permissions,
      isExternalAccount: user?.is_external_account === true,
    });
  }, [
    activeRole,
    user?.isLoggedIn,
    user?.is_external_account,
    user?.permissions,
    user?.role,
  ]);

  const [latestAnnouncements, setLatestAnnouncements] = useState([]);
  const [popupCandidateAnnouncements, setPopupCandidateAnnouncements] = useState([]);
  const [latestMeta, setLatestMeta] = useState({ total: 0, limit: 5 });
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [popupAnnouncements, setPopupAnnouncements] = useState([]);
  const [popupAckToken, setPopupAckToken] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchFeed = useCallback(
    async ({ limit = 5, offset = 0 } = {}) => {
      if (!activeRole) return { announcements: [], total: 0 };
      try {
        return await listAnnouncementFeed({
          limit: Math.max(1, Math.min(limit, 100)),
          offset: Math.max(0, offset || 0),
        });
      } catch (error) {
        throw new Error(error?.message || 'お知らせの取得に失敗しました');
      }
    },
    [activeRole]
  );

  const refreshFeed = useCallback(async () => {
    if (!activeRole) {
      setLatestAnnouncements([]);
      setPopupCandidateAnnouncements([]);
      setPopupAnnouncement(null);
      setPopupAnnouncements([]);
      setFeedError('');
      setFeedLoading(false);
      return { announcements: [] };
    }
    setFeedLoading(true);
    setFeedError('');
    try {
      const data = await fetchFeed({ limit: POPUP_FEED_LIMIT });
      const announcements = data?.announcements || [];
      setLatestAnnouncements(announcements.slice(0, LATEST_FEED_LIMIT));
      setPopupCandidateAnnouncements(announcements);
      setLatestMeta({ total: data?.total || 0, limit: LATEST_FEED_LIMIT });
      setLastSyncedAt(Date.now());
      return data;
    } catch (error) {
      setLatestAnnouncements([]);
      setPopupCandidateAnnouncements([]);
      setPopupAnnouncements([]);
      setLatestMeta({ total: 0, limit: 5 });
      setFeedError(error?.message || 'お知らせの取得に失敗しました');
      throw error;
    } finally {
      setFeedLoading(false);
    }
  }, [activeRole, fetchFeed]);

  const refreshPendingCount = useCallback(async () => {
    if (!activeRole || activeRole !== 'admin' || !canViewAdminAnnouncements) {
      setPendingApprovalCount(0);
      return 0;
    }
    try {
      const payload = await getAdminAnnouncementPendingCount();
      const count = Number(payload?.pendingCount || 0);
      setPendingApprovalCount(count);
      return count;
    } catch (error) {
      console.warn('[AnnouncementContext] pending count fetch failed', error);
      setPendingApprovalCount(0);
      return 0;
    }
  }, [activeRole, canViewAdminAnnouncements]);

  useEffect(() => {
    if (!activeRole) {
      setPopupAckToken(null);
      setPopupAnnouncement(null);
      setPopupAnnouncements([]);
      return;
    }
    setPopupAckToken(readCookie(buildCookieName(activeRole)));
    refreshFeed().catch(() => {});
    refreshPendingCount().catch(() => {});
  }, [activeRole, refreshFeed, refreshPendingCount]);

  const evaluatePopupCandidate = useCallback(
    (announcements) => {
      if (!activeRole) {
        setPopupAnnouncement(null);
        setPopupAnnouncements([]);
        return;
      }
      const candidates = announcements.filter((item) => {
        if (
          !item?.popupEnabled ||
          item?.status !== 'published' ||
          item?.popupActive === false ||
          item?.popupExpired === true ||
          item?.viewerAcknowledged === true
        ) {
          return false;
        }
        const token = buildPopupToken(item);
        return token && token !== popupAckToken;
      });
      setPopupAnnouncements(candidates);
      if (!candidates.length) {
        setPopupAnnouncement(null);
        return;
      }
      setPopupAnnouncement(candidates[0]);
    },
    [activeRole, popupAckToken]
  );

  useEffect(() => {
    evaluatePopupCandidate(popupCandidateAnnouncements);
  }, [popupCandidateAnnouncements, evaluatePopupCandidate]);

  const acknowledgePopup = useCallback(async (targetAnnouncement = popupAnnouncement) => {
    if (!targetAnnouncement || !activeRole) return;
    const token = buildPopupToken(targetAnnouncement);
    if (!token) return;
    setPopupAnnouncements((prev) => prev.filter((item) => item.id !== targetAnnouncement.id));
    setPopupCandidateAnnouncements((prev) =>
      prev.map((item) =>
        item.id === targetAnnouncement.id
          ? { ...item, viewerAcknowledged: true, acknowledgedAt: new Date().toISOString() }
          : item
      )
    );
    setLatestAnnouncements((prev) =>
      prev.map((item) =>
        item.id === targetAnnouncement.id
          ? { ...item, viewerAcknowledged: true, acknowledgedAt: new Date().toISOString() }
          : item
      )
    );
    setPopupAnnouncement((prev) => (prev?.id === targetAnnouncement.id ? null : prev));
    try {
      const payload = await acknowledgeAnnouncement(Number(targetAnnouncement.id));
      if (payload?.announcement) {
        setLatestAnnouncements((prev) =>
          prev.map((item) => (item.id === payload.announcement.id ? payload.announcement : item))
        );
        setPopupCandidateAnnouncements((prev) =>
          prev.map((item) => (item.id === payload.announcement.id ? payload.announcement : item))
        );
      } else {
        setLatestAnnouncements((prev) =>
          prev.map((item) =>
            item.id === targetAnnouncement.id
              ? { ...item, viewerAcknowledged: true, acknowledgedAt: new Date().toISOString() }
              : item
          )
        );
        setPopupCandidateAnnouncements((prev) =>
          prev.map((item) =>
            item.id === targetAnnouncement.id
              ? { ...item, viewerAcknowledged: true, acknowledgedAt: new Date().toISOString() }
              : item
          )
        );
      }
    } catch (error) {
      console.warn('[AnnouncementContext] popup acknowledge failed', error);
      return;
    }
    writeCookie(buildCookieName(activeRole), token, POPUP_COOKIE_TTL_DAYS);
    setPopupAckToken(token);
  }, [popupAnnouncement, activeRole]);

  const dismissPopup = useCallback(() => {
    setPopupAnnouncement(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      role: activeRole,
      latestAnnouncements,
      feedLoading,
      feedError,
      lastSyncedAt,
      refreshFeed,
      fetchFeed,
      pendingApprovalCount,
      refreshPendingCount,
      popupAnnouncement,
      popupAnnouncements,
      acknowledgePopup,
      dismissPopup,
      latestMeta,
    }),
    [
      activeRole,
      latestAnnouncements,
      feedLoading,
      feedError,
      lastSyncedAt,
      refreshFeed,
      fetchFeed,
      pendingApprovalCount,
      refreshPendingCount,
      popupAnnouncement,
      popupAnnouncements,
      acknowledgePopup,
      dismissPopup,
      latestMeta,
    ]
  );

  return (
    <AnnouncementContext.Provider value={contextValue}>{children}</AnnouncementContext.Provider>
  );
}

export const useAnnouncements = () => {
  const ctx = useContext(AnnouncementContext);
  if (!ctx) {
    throw new Error('useAnnouncements must be used within AnnouncementProvider');
  }
  return ctx;
};
